<script setup>
import { computed, ref, defineProps, defineEmits } from "vue";

const props = defineProps({
  isCollapsed: Boolean,
  selectedCategory: String,
});
const emit = defineEmits(["category-change"]);
const setActiveIcon = (icon) => {
  emit("category-change", icon);
};

const showTooltip = ref(false);
const showTooltipOrder = ref(false);
const showTooltipPush = ref(false);

// Позиции для телепортированных тултипов
const tooltipPosition = ref({ x: 80, y: 130 });
const orderTooltipPosition = ref({ x: 80, y: 190 });
const pushTooltipPosition = ref({ x: 80, y: 245 });

// Обновляем позицию тултипа при наведении
const updateTooltipPosition = (event, positionRef) => {
  const element = event.currentTarget;
  const rect = element.getBoundingClientRect();

  positionRef.value = {
    x: rect.right + 10, // 10px отступ от кнопки
    y: rect.top + rect.height / 2, // центрирование по вертикали
  };
};
const isActive = computed(() => (icon) => {
  if (icon === "dashboard") {
    return !props.isCollapsed;
  }
  if (props.isCollapsed) {
    return false;
  }
  return props.selectedCategory === icon;
});
</script>

<template>
  <div class="monitoring-panel">
    <nav class="side-nav">
      <button
        class="nav-item"
        @click.stop="setActiveIcon('dashboard')"
        @mouseenter="
          showTooltip = true;
          updateTooltipPosition($event, tooltipPosition);
        "
        @mouseleave="showTooltip = false"
      >
        <Teleport to="body" v-if="showTooltip">
          <div
            class="tooltip-right"
            :style="{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translateY(-50%)',
            }"
          >
            <div class="tooltip-content">Свернуть/развернуть</div>
          </div>
        </Teleport>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.1891 6.70697L9.77506 5.29297L4.48206 10.586C4.10711 10.961 3.89648 11.4696 3.89648 12C3.89648 12.5303 4.10711 13.0389 4.48206 13.414L9.77506 18.707L11.1891 17.293L5.90006 12L11.1891 6.70697Z"
            fill="#4D5460"
          />
          <path
            d="M18.1892 6.70697L16.7752 5.29297L10.7752 11.293C10.5877 11.4805 10.4824 11.7348 10.4824 12C10.4824 12.2651 10.5877 12.5194 10.7752 12.707L16.7752 18.707L18.1892 17.293L12.9002 12L18.1892 6.70697Z"
            fill="#4D5460"
          />
        </svg>
      </button>
      <button
        class="nav-item"
        :class="{ active: isActive('orders') }"
        @click.stop="setActiveIcon('orders')"
        @mouseenter="
          showTooltipOrder = true;
          updateTooltipPosition($event, orderTooltipPosition);
        "
        @mouseleave="showTooltipOrder = false"
      >
        <Teleport to="body" v-if="showTooltipOrder">
          <div
            class="tooltip-right"
            :style="{
              left: `${orderTooltipPosition.x}px`,
              top: `${orderTooltipPosition.y}px`,
              transform: 'translateY(-50%)',
            }"
          >
            <div class="tooltip-content">Заказы</div>
          </div>
        </Teleport>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_444_9760)">
            <path
              d="M15.8333 4.16634H14.1667V3.33301C14.1667 2.66997 13.9033 2.03408 13.4344 1.56524C12.9656 1.0964 12.3297 0.833008 11.6667 0.833008H2.5C1.83696 0.833008 1.20107 1.0964 0.732233 1.56524C0.263392 2.03408 0 2.66997 0 3.33301L0 15.833H1.70083C1.67939 15.9709 1.66797 16.1101 1.66667 16.2497C1.66667 17.0232 1.97396 17.7651 2.52094 18.3121C3.06792 18.859 3.80979 19.1663 4.58333 19.1663C5.35688 19.1663 6.09875 18.859 6.64573 18.3121C7.19271 17.7651 7.5 17.0232 7.5 16.2497C7.49869 16.1101 7.48727 15.9709 7.46583 15.833H12.5342C12.5127 15.9709 12.5013 16.1101 12.5 16.2497C12.5 17.0232 12.8073 17.7651 13.3543 18.3121C13.9013 18.859 14.6431 19.1663 15.4167 19.1663C16.1902 19.1663 16.9321 18.859 17.4791 18.3121C18.026 17.7651 18.3333 17.0232 18.3333 16.2497C18.332 16.1101 18.3206 15.9709 18.2992 15.833H20V8.33301C19.9987 7.22834 19.5593 6.16931 18.7782 5.38819C17.997 4.60708 16.938 4.16766 15.8333 4.16634V4.16634ZM15.8333 5.83301C16.4964 5.83301 17.1323 6.0964 17.6011 6.56524C18.0699 7.03408 18.3333 7.66997 18.3333 8.33301V9.16634H14.1667V5.83301H15.8333ZM5.83333 16.2497C5.83333 16.5812 5.70164 16.8991 5.46722 17.1336C5.2328 17.368 4.91485 17.4997 4.58333 17.4997C4.25181 17.4997 3.93387 17.368 3.69945 17.1336C3.46503 16.8991 3.33333 16.5812 3.33333 16.2497C3.33385 16.1072 3.3601 15.9661 3.41083 15.833H5.75583C5.80657 15.9661 5.83282 16.1072 5.83333 16.2497V16.2497ZM12.5 14.1663H1.66667V3.33301C1.66667 3.11199 1.75446 2.90003 1.91074 2.74375C2.06702 2.58747 2.27899 2.49967 2.5 2.49967H11.6667C11.8877 2.49967 12.0996 2.58747 12.2559 2.74375C12.4122 2.90003 12.5 3.11199 12.5 3.33301V14.1663ZM16.6667 16.2497C16.6667 16.5812 16.535 16.8991 16.3005 17.1336C16.0661 17.368 15.7482 17.4997 15.4167 17.4997C15.0851 17.4997 14.7672 17.368 14.5328 17.1336C14.2984 16.8991 14.1667 16.5812 14.1667 16.2497C14.167 16.1072 14.1933 15.966 14.2442 15.833H16.5892C16.64 15.966 16.6663 16.1072 16.6667 16.2497V16.2497ZM14.1667 14.1663V10.833H18.3333V14.1663H14.1667Z"
              fill="currentColor"
            />
          </g>
          <defs>
            <clipPath id="clip0_444_9760">
              <rect width="20" height="20" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </button>
      <button
        class="nav-item"
        :class="{ active: isActive('notifications') }"
        @click.stop="setActiveIcon('notifications')"
        @mouseenter="
          showTooltipPush = true;
          updateTooltipPosition($event, pushTooltipPosition);
        "
        @mouseleave="showTooltipPush = false"
      >
        <Teleport to="body" v-if="showTooltipPush">
          <div
            class="tooltip-right"
            :style="{
              left: `${pushTooltipPosition.x}px`,
              top: `${pushTooltipPosition.y}px`,
              transform: 'translateY(-50%)',
            }"
          >
            <div class="tooltip-content">Push</div>
          </div>
        </Teleport>

        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_444_9764)">
            <path
              d="M19.3822 13.5L17.2155 5.69081C16.7529 4.02033 15.7447 2.5526 14.3516 1.52122C12.9585 0.489841 11.2604 -0.045916 9.52758 -0.000782941C7.79479 0.0443501 6.1269 0.66778 4.78934 1.77028C3.45178 2.87279 2.52143 4.39101 2.14635 6.08331L0.470519 13.625C0.389429 13.9903 0.391407 14.3693 0.476307 14.7338C0.561206 15.0983 0.72686 15.4391 0.961047 15.731C1.19523 16.0229 1.49198 16.2586 1.82939 16.4205C2.1668 16.5824 2.53626 16.6666 2.91052 16.6666H5.91635C6.10761 17.6086 6.61863 18.4554 7.3628 19.0636C8.10698 19.6719 9.03856 20.0042 9.99969 20.0042C10.9608 20.0042 11.8924 19.6719 12.6366 19.0636C13.3807 18.4554 13.8918 17.6086 14.083 16.6666H16.9747C17.3598 16.6665 17.7396 16.5773 18.0846 16.4062C18.4296 16.2351 18.7303 15.9865 18.9634 15.68C19.1966 15.3735 19.3557 15.0173 19.4285 14.6391C19.5013 14.261 19.4857 13.8711 19.383 13.5H19.3822ZM9.99969 18.3333C9.48447 18.3312 8.98249 18.1699 8.56244 17.8716C8.14238 17.5732 7.82475 17.1524 7.65302 16.6666H12.3464C12.1746 17.1524 11.857 17.5732 11.4369 17.8716C11.0169 18.1699 10.5149 18.3312 9.99969 18.3333ZM17.6372 14.6708C17.5594 14.7739 17.4587 14.8573 17.3429 14.9145C17.2271 14.9716 17.0996 15.0009 16.9705 15H2.91052C2.78574 15 2.66255 14.9719 2.55006 14.9179C2.43756 14.8639 2.33863 14.7854 2.26056 14.688C2.1825 14.5907 2.12729 14.477 2.09901 14.3555C2.07073 14.234 2.07011 14.1076 2.09719 13.9858L3.77302 6.44415C4.06831 5.11599 4.79911 3.92465 5.84923 3.05952C6.89936 2.19439 8.20854 1.70512 9.56866 1.66949C10.9288 1.63386 12.2618 2.05391 13.3558 2.86287C14.4498 3.67183 15.2419 4.82327 15.6064 6.13415L17.773 13.9433C17.8084 14.0668 17.8145 14.1968 17.791 14.3231C17.7674 14.4494 17.7147 14.5684 17.6372 14.6708Z"
              fill="currentColor"
            />
          </g>
          <defs>
            <clipPath id="clip0_444_9764">
              <rect width="20" height="20" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </button>
    </nav>
  </div>
</template>

<style scoped lang="scss">
.monitoring-panel {
  display: flex;
  height: 100vh;
  // background: var(--body-color);
  background: #fff;
}

.side-nav {
  display: flex;
  flex-direction: column;
  width: 46px;
  row-gap: 11px;
}

.tooltip-right {
  position: absolute;
  left: 100%;
  right: auto;
  top: 50%;
  transform: translateY(-50%);
  background-color: #000000;
  color: #ffffff;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  font-family: Open Sans, sans-serif;
  z-index: 9999;
  width: max-content;
  max-width: 200px;
  margin-right: 10px;
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 1.4;
  text-align: left;
  pointer-events: none;
  &:after {
    content: "";
    position: absolute;
    top: 50%;
    right: 100%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid #000000;
  }
}

.nav-item {
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 46px;
  background: none;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;

  svg {
    width: 20px;
    height: 20px;
    color: #a6a9af;
  }

  &:hover {
    // background-color: var(--card-bg);
    background-color: #f6f6f6;
    border-radius: 6px;
    svg {
      // color: var(--text-color);
      color: #4d5460;
    }
  }

  &.active {
    // background-color: var(--card-bg);
    background-color: #f6f6f6;
    border-radius: 6px;

    svg {
      // color: var(--text-color);
      color: #4d5460;
    }
  }

  &:first-child.active {
    // background-color: var(--card-bg);
    background-color: #f6f6f6;
    border-radius: 6px;

    svg {
      // color: var(--text-color);
      color: #4d5460;
    }
  }
}
</style>
